import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from 'gatsby-source-contentful/rich-text';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import React from 'react';

import { generateCorrectSlug } from '../../helpers/generate-correct-slug';
import { isBrowser } from '../../helpers/is-browser';
import { calculatePrefixPath } from '../../helpers/rendered-page-creation/calculate-prefixed-path';
import { allowedHostsInRichText } from '../../helpers/utils';
import { IMeetingPlaceLinkToAnyPage } from '../../queries/fragments/meetingPlaceLinkToAnyPage';
import IframeWrapper from '../iframe-wrapper/iframe-wrapper';
import ImageWrapper from '../image-wrapper/Image-wrapper';
import LinkButton from '../ui/link-button/link-button';
import LinkWrapper from '../ui/link-wrapper/link-wrapper';
interface RichTextProps {
  richText: RenderRichTextData<ContentfulRichTextGatsbyReference>;
  characterLimitCount?: number;
}

export const RichText = ({ richText, characterLimitCount }: RichTextProps) => {
  let totalCharacterCount = 0;

  const shouldRenderContent = () => {
    return !characterLimitCount || totalCharacterCount < characterLimitCount;
  };

  const options: Options = {
    renderText: (text) => {
      if (characterLimitCount) {
        const remainingCharacters = characterLimitCount - totalCharacterCount;
        if (remainingCharacters > 0) {
          const slicedText = text.slice(0, remainingCharacters);
          totalCharacterCount += slicedText.length;
          const formattedText = slicedText
            .split('\n')
            .flatMap((t, i) => [i > 0 && <br key={i} />, t]);
          // add three dots at the end.
          if (totalCharacterCount >= characterLimitCount) {
            formattedText.push(<span key="ellipsis">...</span>);
          }
          return formattedText;
        }
        return [];
      } else {
        return text.split('\n').flatMap((t, i) => [i > 0 && <br key={i} />, t]);
      }
    },
    renderNode: {
      [BLOCKS.HEADING_2]: (_, children) => {
        return (
          <h2 className="mb-4 font-title text-headingm text-text-default lg:mb-8 lg:text-headingxl">
            {children}
          </h2>
        );
      },
      [BLOCKS.HEADING_3]: (_, children) => {
        return (
          <h3 className="mb-4 font-title text-headings text-text-default lg:mb-4 lg:text-headingm">
            {children}
          </h3>
        );
      },
      [BLOCKS.HEADING_4]: (_, children) => {
        return (
          <h4 className="mb-4 font-title text-headings text-text-default lg:text-headings">
            {children}
          </h4>
        );
      },
      [BLOCKS.PARAGRAPH]: (_, children) =>
        children != '' ? <p>{children}</p> : '',
      [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
        if (!shouldRenderContent()) return null;
        if (node?.data?.target?.file?.url?.includes('.mp4')) {
          return (
            <div className="flex w-full items-center justify-center">
              <div className="w-full">
                <video
                  className="mb-8 w-full"
                  controls
                  src={node?.data?.target?.file?.url}
                />
              </div>
            </div>
          );
        } else
          return (
            <ImageWrapper
              alt={node?.data?.target?.description ?? ''}
              file={node?.data?.target?.file}
              gatsbyImageData={node?.data?.target?.gatsbyImageData}
              svg={node?.data?.target?.svg}
              className="mb-8 transform-gpu rounded-xl md:rounded-xxl"
            />
          );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
        if (!shouldRenderContent()) return null;
        const target = node?.data?.target;
        const type = target?.__typename;
        const isExternalLink = target?.externalLink?.length > 0;
        const isInternalLink = target?.internalLink?.slug?.length > 0;
        const image = target?.image;
        if (
          type === 'ContentfulBlockLink' &&
          (target?.linkText?.length > 0 || image.file?.url?.length > 0) &&
          (target?.externalLink?.length > 0 ||
            target?.internalLink?.slug?.length > 0)
        ) {
          const openInNewWindow = target?.openInNewWindow;
          let url = '';
          if (isExternalLink) {
            url = target?.externalLink;
          } else if (isInternalLink) {
            let pageSlug;
            const nodeSlug = target?.internalLink.slug;
            const subType = target?.internalLink.__typename;
            if (subType === 'ContentfulLibraryPopupModal') {
              url =
                (isBrowser &&
                  calculatePrefixPath(window.location.pathname + nodeSlug)) ||
                '';
              // Generate url using pageSlug if url is empty
              if (!url) {
                pageSlug = nodeSlug;
                url = generateCorrectSlug({
                  customizedSlug: target?.internalLink.customizedSlug,
                  node_locale: target?.internalLink.node_locale,
                  slug: pageSlug,
                  __typename: '',
                } as IMeetingPlaceLinkToAnyPage);
              }
            } else {
              if (subType === 'ContentfulMeetingPlacePageHome') {
                pageSlug = '/';
              } else if (
                subType ===
                  'ContentfulMeetingPlacePageTenantEntertainmentItem' ||
                subType === 'ContentfulMeetingPlacePageTenantFoodBevItem' ||
                subType === 'ContentfulMeetingPlacePageTenantShopItem'
              ) {
                pageSlug = nodeSlug ? nodeSlug : target?.brand.slug;
              } else {
                pageSlug = nodeSlug;
              }
              url = generateCorrectSlug({
                customizedSlug: target?.internalLink.customizedSlug,
                node_locale: target?.internalLink.node_locale,
                slug: pageSlug,
                __typename: '',
              } as IMeetingPlaceLinkToAnyPage);
            }
          }
          return image ? (
            isInternalLink ? (
              <LinkWrapper
                className="mb-8"
                data={target.internalLink}
                target={openInNewWindow ? '_blank' : ''}
              >
                <ImageWrapper
                  alt={image.description ?? ''}
                  file={image.file}
                  gatsbyImageData={image.gatsbyImageData}
                  svg={image.svg}
                  className="mb-8 transform-gpu rounded-xl md:rounded-xxl"
                />
              </LinkWrapper>
            ) : (
              isExternalLink && (
                <a
                  href={url}
                  target={openInNewWindow ? '_blank' : ''}
                  rel="noreferrer"
                >
                  <ImageWrapper
                    alt={image.description ?? ''}
                    file={image.file}
                    gatsbyImageData={image.gatsbyImageData}
                    svg={image.svg}
                    className="mb-8 transform-gpu rounded-xl md:rounded-xxl"
                  />
                </a>
              )
            )
          ) : (
            url && (
              <LinkButton
                className="mb-8"
                to={url}
                openInNewTab={openInNewWindow}
                isExternalLink={isExternalLink}
              >
                {target?.linkText}
              </LinkButton>
            )
          );
        }
      },
      [INLINES.ENTRY_HYPERLINK]: (node: any) => {
        if (!shouldRenderContent()) return null;
        const target = node?.data?.target;
        const type = target?.__typename;
        const nodeSlug = target?.slug;
        let pageSlug;
        if (type === 'ContentfulMeetingPlacePageHome') {
          pageSlug = '/';
        } else if (
          type === 'ContentfulMeetingPlacePageTenantEntertainmentItem' ||
          type === 'ContentfulMeetingPlacePageTenantFoodBevItem' ||
          type === 'ContentfulMeetingPlacePageTenantShopItem'
        ) {
          pageSlug = nodeSlug ? nodeSlug : target?.brand.slug;
        } else {
          pageSlug = nodeSlug;
        }

        return (
          target && ( // Null check on target prevents site to crash if link to a page in draft status.
            <LinkWrapper
              data={{
                customizedSlug: target?.customizedSlug,
                name: target?.name,
                node_locale: target?.node_locale,
                slug: pageSlug,
                __typename: '',
              }}
              className="mb-4"
            >
              {node.content[0]?.value}
            </LinkWrapper>
          )
        );
      },
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        if (!shouldRenderContent()) return null;
        if (node?.data.target?.file?.url) {
          return (
            <a
              href={node.data.target.file.url}
              target="_blank"
              rel="noreferrer"
            >
              {children}
            </a>
          );
        }
      },
      [INLINES.HYPERLINK]: (node: any) => {
        if (!shouldRenderContent()) return null;
        const isAllowedHost = allowedHostsInRichText.some((host) =>
          node.data.uri.includes(host),
        );
        if (node.data.uri.includes('vimeo') && isAllowedHost) {
          return (
            <IframeWrapper>
              <iframe
                title={node.content[0]?.value}
                src={node.data.uri}
                className="border-none"
                allowFullScreen
              ></iframe>
            </IframeWrapper>
          );
        } else if (node.data.uri.includes('youtube') && isAllowedHost) {
          const videoEmbedUrl = node.data.uri.includes('youtube.com/embed')
            ? node.data.uri
            : `https://www.youtube.com/embed/${new URL(
                node.data.uri,
              ).searchParams.get('v')}`;
          return (
            <IframeWrapper>
              <iframe
                title={node.content[0]?.value}
                src={videoEmbedUrl}
                allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                className="border-none"
                allowFullScreen
              ></iframe>
            </IframeWrapper>
          );
        } else if (node.content[0]?.value) {
          return (
            <a
              className="mb-4"
              href={node.data.uri}
              target="_blank"
              rel="noopener noreferrer"
            >
              {node.content[0]?.value}
            </a>
          );
        }
      },
      [BLOCKS.UL_LIST]: (_, children) => {
        if (!shouldRenderContent()) return null;
        const listItems = React.Children.toArray(children);
        if (listItems && listItems.filter(Boolean).length > 0) {
          // Render ul only if there are non-empty list items
          return <ul className="">{listItems}</ul>;
        }
        return null;
      },
      [BLOCKS.OL_LIST]: (_, children) => {
        if (!shouldRenderContent()) return null;
        const listItems = React.Children.toArray(children);
        if (listItems && listItems.filter(Boolean).length > 0) {
          // Render ol only if there are non-empty list items
          return <ol className="">{listItems}</ol>;
        }
        return null;
      },
      [BLOCKS.LIST_ITEM]: (_, children) => {
        if (!shouldRenderContent()) return null;
        const content = React.Children.toArray(children);
        if (content && content.filter(Boolean).length > 0) {
          // Render li only if there is content
          return <li>{content}</li>;
        }
        return null;
      },
      [BLOCKS.TABLE]: (_, children) => {
        return <table className="mb-8">{children}</table>;
      },
      [BLOCKS.TABLE_ROW]: (_, children) => {
        return <tr>{children}</tr>;
      },
      [BLOCKS.TABLE_HEADER_CELL]: (_, children) => {
        return <th className="border px-4 py-2 font-bold">{children}</th>;
      },
      [BLOCKS.TABLE_CELL]: (_, children) => {
        return <td className="border px-4 py-2">{children}</td>;
      },
    },
  };
  return (
    <div className="richText">
      {renderRichText(richText, options)}
      {/* The following empty div resets the bottom margin of the box in mobile. It's a bit of a hack. */}
      <div className="h-px"></div>
    </div>
  );
};
